import {FOLLOWERS_COLLECTION, followerDocId} from "@buildwithflux/following-schema";
import {STARRED_COLLECTION, starredDocId} from "@buildwithflux/starring-schema";
import {useCallback, useEffect, useState} from "react";

import {getFirebaseClient} from "../firebase/firebaseClient";

interface RelationshipState {
    /** `undefined` until the subscription resolves (button stays disabled). */
    readonly active: boolean | undefined;
    /** Optimistically override the rendered state before the write lands. */
    readonly setOptimistic: (next: boolean) => void;
}

interface DocExistsState {
    readonly key: string | null;
    readonly active: boolean | undefined;
}

/**
 * Subscribe to the existence of a single per-viewer Firestore doc (realtime,
 * no HTTP). Returns `undefined` while loading, then `true`/`false`.
 *
 * State is keyed by collection/docId, so a doc change (e.g. a viewer switch)
 * never renders the previous doc's state as the current one — even for the
 * render in which the key changes.
 *
 * SSR-safe: Firebase is only touched inside the effect, so the server renders
 * the loading state and the client resolves it after hydration.
 */
function useDocExists(collection: string | null, docId: string | null): RelationshipState {
    const key = collection && docId ? `${collection}/${docId}` : null;
    const [state, setState] = useState<DocExistsState>({key, active: undefined});

    if (state.key !== key) {
        setState({key, active: undefined});
    }

    useEffect(() => {
        if (!collection || !docId) {
            return;
        }

        const effectKey = `${collection}/${docId}`;
        let cancelled = false;
        let unsubscribe: (() => void) | undefined;

        void (async () => {
            const {db, firestoreModule} = await getFirebaseClient();
            if (cancelled) return;
            const ref = firestoreModule.doc(db, collection, docId);
            unsubscribe = firestoreModule.onSnapshot(
                ref,
                (snap) => {
                    if (!cancelled) setState({key: effectKey, active: snap.exists()});
                },
                () => {
                    if (!cancelled) setState({key: effectKey, active: false});
                },
            );
        })();

        return () => {
            cancelled = true;
            unsubscribe?.();
        };
    }, [collection, docId]);

    const setOptimistic = useCallback((next: boolean) => {
        setState((previous) => ({key: previous.key, active: next}));
    }, []);

    return {active: state.key === key ? state.active : undefined, setOptimistic};
}

/** Realtime "does the viewer follow this account" state. */
export function useFollowState(accountUid: string, viewerUid: string | null): RelationshipState {
    return useDocExists(
        viewerUid ? FOLLOWERS_COLLECTION : null,
        viewerUid ? followerDocId(accountUid, viewerUid) : null,
    );
}

/** Realtime "has the viewer starred this document" state. */
export function useStarState(documentUid: string, viewerUid: string | null): RelationshipState {
    return useDocExists(viewerUid ? STARRED_COLLECTION : null, viewerUid ? starredDocId(documentUid, viewerUid) : null);
}
